interface TrickCountIndicatorProps {
  nsTricksWon: number;
  ewTricksWon: number;
  contract: string;
  contractDirection: string;
}

function formatContract(contract: string, direction: string) {
  if (!contract) return null;
  const level = contract.charAt(0);
  const suitPart = contract.substring(1).toUpperCase();

  // Suit colors for dark-on-gray contract box
  const suitMap: Record<string, { symbol: string; color: string }> = {
    S: { symbol: '\u2660', color: 'text-ink' },
    H: { symbol: '\u2665', color: 'text-red-600' },
    D: { symbol: '\u2666', color: 'text-red-600' },
    C: { symbol: '\u2663', color: 'text-ink' },
  };

  if (suitPart === 'NT') {
    return (
      <>
        {level}NT<br />{direction}
      </>
    );
  }

  const suitInfo = suitMap[suitPart];
  if (suitInfo) {
    return (
      <>
        {level}<span className={`${suitInfo.color} font-suits`}>{suitInfo.symbol}</span><br />{direction}
      </>
    );
  }

  return <>{contract}<br />{direction}</>;
}

/**
 * L-shaped trick count indicator matching BBO style.
 *
 * Layout (from reference image 11_0_i2):
 * ┌────┬────────────┐
 * │    │  Contract   │
 * │ NS │  Direction  │
 * │    ├────────────┤
 * │    │     EW     │
 * └────┴────────────┘
 *
 * NS count spans full height on the left.
 * Contract is top-right (gray bg, black text).
 * EW count is bottom-right (same width as contract).
 */
export function TrickCountIndicator({
  nsTricksWon,
  ewTricksWon,
  contract,
  contractDirection,
}: TrickCountIndicatorProps) {
  return (
    <div className="inline-grid overflow-hidden rounded font-bold shadow-sm leading-none"
      style={{ gridTemplateColumns: 'auto 1fr', gridTemplateRows: '1fr auto' }}
    >
      {/* NS tricks — left column, spans both rows.
          Feature 028 / T020: `bg-teal-700 text-white` until 2026-09-07 — the only raw Tailwind
          palette colour left in the counting folder, and the reason this indicator would have
          stayed teal on a table that had turned maroon. `felt-deep` and `felt` resolve through
          `--felt-deep` / `--felt`, so a surface that redefines those redefines this too; that is
          how `.surface-mastery` (index.css, inert until the Mastery round ships) already turns
          the rest of the table claret. Nothing changes on the rebuilt Counting page, which had
          overridden both boxes to exactly these tokens since the redesign (index.css:1739). */}
      <div className="row-span-2 bg-felt-deep text-cream flex items-center justify-center counting-trick-ns">
        {nsTricksWon}
      </div>
      {/* Contract + direction — top right, gray bg with black text (BBO style) */}
      <div className="bg-cream text-ink text-center font-bold leading-tight counting-trick-contract">
        {formatContract(contract, contractDirection)}
      </div>
      {/* EW tricks — bottom right, same width as contract. The lighter of the pair, so the two
          counts stay tellable apart at a glance; `felt` sits above `felt-deep` the same way
          `teal-600` sat above `teal-700`.
          This half also carried a contrast fault: white on `teal-600` (#0d9488) is 3.83:1, and the
          digits top out at 18px bold — under the 18.66px that would let 3:1 apply — so it was
          below AA wherever the legacy table draws it. Cream on `--felt` is ~8.5:1. */}
      <div className="bg-felt text-cream text-center counting-trick-ew">
        {ewTricksWon}
      </div>
    </div>
  );
}
